import { auth, db } from './firebase';
import { collection, getDocs, doc, setDoc, getDoc, getDocFromServer, serverTimestamp, runTransaction, updateDoc, deleteField, addDoc } from 'firebase/firestore';
import useAppStore from '../store/appStore'; 
import useAdminStore from '../store/adminStore'; 
import { safeClone, defaultClientData, sanitizeClientData, mergeLocalLeistungSnapshots, applyPendingFestsetzungen, injectFestsetzungsSnapshots, applyLocalFestsetzungBackup, serializeClientDataForCloud } from '../store/slices/coreDataSlice'; 
import { mergeWithDefaultSettings } from '../store/slices/settingsSlice';
import { assertLocalSessionOwnsWrite, isAdminMaintenanceLockLive } from '../utils/sessionLock';
import {
    rememberLastAdminUpdate,
    guardClientWriteAgainstAdminUpdate,
} from './adminOverwriteGuard';
// TeamPro+-Schwelle: dynamischer Import in recomputeAndPersistFilialTeamProPlus

/** God-Mode Hard-Lock: Mandant darf nicht schreiben, solange Coach-Lock live. */
const rejectIfGodModeLockLive = async (
    targetUid: string,
    isGodMode: boolean,
    label: string
): Promise<boolean> => {
    const godSession = (() => {
        try { return !!sessionStorage.getItem('GOD_MODE_ID'); } catch { return false; }
    })();
    // Coach im God-Mode darf immer schreiben (auch den eigenen Lock)
    if (isGodMode || godSession || !targetUid) return false;
    try {
        const snap = await getDocFromServer(doc(db, 'kalkulation_mandanten', targetUid));
        if (!snap.exists()) return false;
        if (isAdminMaintenanceLockLive(snap.data() as any)) {
            console.warn(`${label}: Schreibvorgang abgebrochen — God-Mode-Lock aktiv`);
            return true;
        }
    } catch (err) {
        // Fail-closed bei Lock-Check: kein Write, wenn Server-Stand unklar
        console.warn(`${label}: Lock-Check fehlgeschlagen — Write abgebrochen`, err);
        return true;
    }
    return false;
};
// Re-Exports für bestehende Imports
export {
    toAdminUpdateMs,
    rememberLastAdminUpdate,
    getKnownLastAdminUpdateMs,
    adminUpdateMandantRoot,
    adminSetMandantRoot,
    touchLastAdminUpdate,
    guardClientWriteAgainstAdminUpdate,
} from './adminOverwriteGuard';

// ---------------------------------------------------------------------------
// Vektor 2: syncEpoch + Debounce-Cancellation (Kontextisolation)
// ---------------------------------------------------------------------------
let syncEpoch = 0;
let pendingAutosyncTimer: ReturnType<typeof setTimeout> | null = null;

export const getSyncEpoch = (): number => syncEpoch;

export const cancelPendingAutosync = (): void => {
    if (pendingAutosyncTimer != null) {
        clearTimeout(pendingAutosyncTimer);
        pendingAutosyncTimer = null;
    }
};

/** Timer killen + Epoch erhöhen. Gibt die neue Epoch zurück. */
export const bumpSyncEpoch = (): number => {
    cancelPendingAutosync();
    syncEpoch += 1;
    return syncEpoch;
};

/**
 * Debounced Autosync mit Epoch-Guard.
 * Feuert nur, wenn die Schedule-Epoch noch die aktuelle syncEpoch ist.
 */
export const scheduleAutosync = (fn: () => void, delayMs: number, epochAtSchedule: number): void => {
    cancelPendingAutosync();
    pendingAutosyncTimer = setTimeout(() => {
        pendingAutosyncTimer = null;
        if (epochAtSchedule !== syncEpoch) {
            console.warn('scheduleAutosync: verworfen — syncEpoch Mismatch (Kontextwechsel)');
            return;
        }
        fn();
    }, delayMs);
};

/**
 * Kontextwechsel (Mandant/Jahr/Filiale/God-Mode):
 * Timer cancel → Epoch++ → Gates sperren → Store auf Defaults flashen.
 */
export const isolateSyncContext = (): number => {
    const epoch = bumpSyncEpoch();
    try {
        sessionStorage.setItem('PE_CLOUD_SYNC_BLOCKED', '1');
        sessionStorage.removeItem('PE_CLOUD_LOAD_OK');
    } catch (_) { /* ignore */ }
    try {
        useAppStore.setState({
            isInitialized: false,
            cloudWriteLock: true,
            isDirty: false,
            past: [],
            future: [],
            clientData: { ...defaultClientData } as any,
            savedSnapshots: [],
        } as any);
        try { (window as any).isSystemDirty = false; } catch (_) { /* ignore */ }
    } catch (_) { /* ignore */ }
    return epoch;
};

// ---------------------------------------------------------------------------
// Vektor 1: Physisches Root-Purge (nur Ops-Whitelist — Stammdaten unberührt)
// ---------------------------------------------------------------------------
const ROOT_OPS_DELETE_FIELD_PATHS = [
    'clientData.bwaData',
    'clientData.unternehmerData',
    'clientData.lohnfaktorData',
    'clientData.mitarbeiterLeistung',
    'clientData.personalData',
    'clientData.unproductivePersonalData',
    'clientData.azubiAssistenzData',
    'clientData.gesamtleistungData',
    'clientData.gesamtleistungSettings',
    'clientData.ulBasisdaten',
    'clientData.ulHalbjahr1',
    'clientData.ulHalbjahr2',
    'clientData.cachedUnternehmerMetrics',
] as const;

/** Ops-Keys, die bei Root-setDoc aus dem Payload gestrippt werden (kein Re-Write). */
const ROOT_OPS_STRIP_KEYS = [
    'bwaData',
    'unternehmerData',
    'lohnfaktorData',
    'mitarbeiterLeistung',
    'personalData',
    'unproductivePersonalData',
    'azubiAssistenzData',
    'gesamtleistungData',
    'gesamtleistungSettings',
    'ulBasisdaten',
    'ulHalbjahr1',
    'ulHalbjahr2',
    'cachedUnternehmerMetrics',
    // bestehende Extra-Strips (nicht in deleteField-Whitelist, aber nie erneut schreiben)
    'drafts',
    'urlaubsverlustData',
    'produktRechnerData',
    'kalkulator',
    'extensionsKalkulator',
    'budgetPlaner',
    'privateCostData',
] as const;

/**
 * Physisches Entfernen verwaister Ops-Blöcke am Root via deleteField().
 * Stammdaten / Billing / Flags bleiben unberührt. Idempotent.
 */
export const purgeLegacyRootOpsData = async (targetUid: string): Promise<boolean> => {
    if (!targetUid) return false;
    try {
        const rootRef = doc(db, 'kalkulation_mandanten', targetUid);
        const payload: Record<string, ReturnType<typeof deleteField>> = {};
        for (const path of ROOT_OPS_DELETE_FIELD_PATHS) {
            payload[path] = deleteField();
        }
        await updateDoc(rootRef, payload as any);
        return true;
    } catch (err) {
        // Doc fehlt / Permission: kein Crash — Purge ist Best-Effort
        console.warn('purgeLegacyRootOpsData: fehlgeschlagen', err);
        return false;
    }
};

export interface FestsetzungVerifyContext {
    employeeId: string;
    halbjahrKey: 'halbjahr1' | 'halbjahr2';
    monatIndex: number;
}

export interface FestsetzungPersistPayload {
    verifyContext: FestsetzungVerifyContext;
    snapshot?: Record<string, any>;
    lockedAt?: string;
    lockedBy?: string;
    isLocked: boolean;
}

const parseSnapshotValue = (value: any) => {
    if (!value) return null;
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }
    return value;
};

const readFestsetzungFromDoc = (docData: any, verifyContext: FestsetzungVerifyContext) => {
    const idxKey = String(verifyContext.monatIndex);
    const dedicatedRaw = docData?.festsetzungsSnapshots?.[verifyContext.employeeId]?.[verifyContext.halbjahrKey]?.[idxKey]
        ?? docData?.festsetzungsSnapshots?.[verifyContext.employeeId]?.[verifyContext.halbjahrKey]?.[verifyContext.monatIndex];
    const dedicated = parseSnapshotValue(dedicatedRaw);
    if (dedicated) return dedicated;

    const empData = docData?.clientData?.mitarbeiterLeistung?.[verifyContext.employeeId];
    if (empData?.lockedMetrics_string) {
        try {
            const lm = JSON.parse(empData.lockedMetrics_string);
            const fromString = lm?.[verifyContext.halbjahrKey]?.[idxKey]
                ?? lm?.[verifyContext.halbjahrKey]?.[verifyContext.monatIndex];
            if (fromString) return fromString;
        } catch {
            // ignore
        }
    }

    return docData?.clientData
        ?.mitarbeiterLeistung?.[verifyContext.employeeId]
        ?.lockedMetrics?.[verifyContext.halbjahrKey]?.[idxKey]
        ?? docData?.clientData
            ?.mitarbeiterLeistung?.[verifyContext.employeeId]
            ?.lockedMetrics?.[verifyContext.halbjahrKey]?.[verifyContext.monatIndex];
};

const hasFestsetzungSnapshot = (monthSnap: any): boolean => Boolean(
    monthSnap && (
        monthSnap.sollUmsatzMonat !== undefined
        || monthSnap.arbeitstage !== undefined
        || monthSnap.stdLohnGesamt !== undefined
        || monthSnap.effektiverBruttolohn !== undefined
    )
);

const buildFestsetzungsSnapshotsFromClientData = (clientData: any): Record<string, any> => {
    const result: Record<string, any> = {};
    const ml = clientData?.mitarbeiterLeistung || {};

    Object.keys(ml).forEach((empId) => {
        const emp = ml[empId];
        const lm = emp?.lockedMetrics;
        if (!lm || (lm.isLocked === true && lm.metrics)) return;

        ['halbjahr1', 'halbjahr2'].forEach((hjKey) => {
            if (!lm[hjKey] || typeof lm[hjKey] !== 'object') return;
            Object.keys(lm[hjKey]).forEach((idxKey) => {
                const snap = lm[hjKey][idxKey];
                if (!hasFestsetzungSnapshot(snap)) return;
                if (!result[empId]) result[empId] = { halbjahr1: {}, halbjahr2: {} };
                result[empId][hjKey][idxKey] = snap;
            });
        });
    });

    return result;
};

const writeFestsetzungToDedicatedField = async (
    verifyContext: FestsetzungVerifyContext,
    snapshot: Record<string, any>,
    meta?: { lockedAt?: string; lockedBy?: string }
): Promise<boolean> => {
    try {
        const user = auth.currentUser;
        if (!user) return false;

        const targetUid =
            useAdminStore.getState().impersonatedClientId
            || (() => { try { return sessionStorage.getItem('GOD_MODE_ID'); } catch { return null; } })()
            || user.uid;
        const year = getActiveGeschaeftsjahr();
        let rootData: any = null;
        try {
            const rootSnap = await getDoc(doc(db, 'kalkulation_mandanten', targetUid));
            rootData = rootSnap.exists() ? rootSnap.data() : null;
        } catch (_) { /* ignore */ }
        const yearDocRef = getOperationalYearDocRef(targetUid, year, rootData, useAppStore.getState().clientData);

        await setDoc(yearDocRef, {
            migrationMeta: {
                speicherJahr: year,
                lastFestsetzungAt: serverTimestamp(),
            },
        }, { merge: true });

        const fieldBase = `festsetzungsSnapshots.${verifyContext.employeeId}.${verifyContext.halbjahrKey}.${verifyContext.monatIndex}`;
        const cleanSnapshot = JSON.parse(JSON.stringify({
            ...snapshot,
            isLocked: true,
            lockedAt: meta?.lockedAt,
            lockedBy: meta?.lockedBy || 'Admin',
        }));

        await updateDoc(yearDocRef, {
            [fieldBase]: JSON.stringify(cleanSnapshot),
        });

        return true;
    } catch (error: any) {
        console.error('writeFestsetzungToDedicatedField failed:', error?.message || error);
        return false;
    }
};

const deleteFestsetzungFromDedicatedField = async (
    verifyContext: FestsetzungVerifyContext
): Promise<boolean> => {
    try {
        const user = auth.currentUser;
        if (!user) return false;

        const targetUid =
            useAdminStore.getState().impersonatedClientId
            || (() => { try { return sessionStorage.getItem('GOD_MODE_ID'); } catch { return null; } })()
            || user.uid;
        const year = getActiveGeschaeftsjahr();
        let rootData: any = null;
        try {
            const rootSnap = await getDoc(doc(db, 'kalkulation_mandanten', targetUid));
            rootData = rootSnap.exists() ? rootSnap.data() : null;
        } catch (_) { /* ignore */ }
        const yearDocRef = getOperationalYearDocRef(targetUid, year, rootData, useAppStore.getState().clientData);
        const fieldBase = `festsetzungsSnapshots.${verifyContext.employeeId}.${verifyContext.halbjahrKey}.${verifyContext.monatIndex}`;

        await updateDoc(yearDocRef, {
            [fieldBase]: deleteField(),
        });
        return true;
    } catch (error: any) {
        console.error('deleteFestsetzungFromDedicatedField failed:', error?.message || error);
        return false;
    }
};

export const getActiveGeschaeftsjahr = (clientData?: any): string => {
    const state = useAppStore.getState();
    const data = clientData || state.clientData;
    const years = Array.isArray(data?.geschaeftsjahre) ? data.geschaeftsjahre : [];
    const yearPool = years
        .map((j: any) => String(j?.jahr ?? '').trim())
        .filter(Boolean);

    const sessionYear = sessionStorage.getItem('CURRENT_YEAR');
    const localYear = localStorage.getItem('CURRENT_YEAR');
    const preferred =
        sessionYear ||
        localYear ||
        (data?.aktivesJahr != null ? String(data.aktivesJahr) : '');

    // Stale CURRENT_YEAR (z. B. Kalenderjahr) nie als Schreibziel nutzen, wenn Pool bekannt ist
    if (preferred && (yearPool.length === 0 || yearPool.includes(preferred))) {
        return preferred;
    }
    if (yearPool.length > 0) {
        const latestYear = years.reduce((prev: any, current: any) => (
            Number(prev.jahr) > Number(current.jahr) ? prev : current
        ));
        return String(latestYear.jahr);
    }
    if (data?.jahr) return data.jahr.toString();
    return new Date().getFullYear().toString();
};

/** Root-Registry + Session: aktive Filiale auflösen */
export const getActiveBranchId = (rootOrClient?: any): string => {
    const state = useAppStore.getState();
    const data = rootOrClient || state.clientData || {};
    const sessionBranch = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('CURRENT_BRANCH') : null;
    const localBranch = typeof localStorage !== 'undefined' ? localStorage.getItem('CURRENT_BRANCH') : null;
    if (sessionBranch) return sessionBranch;
    if (localBranch) return localBranch;
    if (data?.activeBranchId) return String(data.activeBranchId);
    const branches = Array.isArray(data?.branches) ? data.branches : [];
    const main = branches.find((b: any) => b?.isMain);
    if (main?.id) return String(main.id);
    if (branches[0]?.id) return String(branches[0].id);
    return 'main';
};

export const resolveBranchMeta = (rootData: any, clientDataHint?: any) => {
    const hasBranches = rootData?.hasBranches === true
        || rootData?.clientData?.hasBranches === true
        || clientDataHint?.hasBranches === true;
    const branches: any[] = Array.isArray(rootData?.branches) && rootData.branches.length
        ? rootData.branches
        : (Array.isArray(rootData?.clientData?.branches) && rootData.clientData.branches.length
            ? rootData.clientData.branches
            : (Array.isArray(clientDataHint?.branches) ? clientDataHint.branches : []));
    const activeBranchId = getActiveBranchId({
        ...clientDataHint,
        ...rootData?.clientData,
        ...rootData,
        branches,
        activeBranchId: rootData?.activeBranchId || rootData?.clientData?.activeBranchId || clientDataHint?.activeBranchId,
    });
    const activeBranch = branches.find((b: any) => String(b?.id) === String(activeBranchId)) || null;
    const isMain = !hasBranches || !activeBranch || activeBranch.isMain === true || String(activeBranchId) === 'main';
    return { hasBranches, branches, activeBranchId, activeBranch, isMain };
};

/** Jahr-Dokument: Hauptsalon = jahre/{jahr}, Nebenfiliale = jahre/{jahr}/filialen/{branchId} */
export const getOperationalYearDocRef = (uid: string, year: string, rootData?: any, clientDataHint?: any) => {
    const meta = resolveBranchMeta(rootData || {}, clientDataHint);
    if (meta.isMain) {
        return doc(db, 'kalkulation_mandanten', uid, 'jahre', year);
    }
    return doc(db, 'kalkulation_mandanten', uid, 'jahre', year, 'filialen', meta.activeBranchId);
};

/** Leere autarke Filial-Jahresakte anlegen (ohne Hauptdaten zu überschreiben) */
export const initializeBranchYearDoc = async (
    uid: string,
    year: string,
    branch: { id: string; name: string; isMain?: boolean },
    identitySeed?: Record<string, any>
): Promise<void> => {
    if (!branch?.id || branch.isMain) return;
    const filialRef = doc(db, 'kalkulation_mandanten', uid, 'jahre', year, 'filialen', branch.id);
    const existing = await getDoc(filialRef);
    if (existing.exists()) return;

    const seedClient = sanitizeClientData({
        ...defaultClientData,
        ...(identitySeed || {}),
        branchName: branch.name || '',
        salonname: branch.name || identitySeed?.salonname || '',
        firmenname: identitySeed?.firmenname || branch.name || '',
        hasBranches: true,
        activeBranchId: branch.id,
    });
    // Schwere Ops-Felder bleiben Defaults — keine Kopie vom Hauptsalon
    delete (seedClient as any).personalData;
    delete (seedClient as any).unproductivePersonalData;
    delete (seedClient as any).azubiAssistenzData;
    delete (seedClient as any).mitarbeiterLeistung;
    delete (seedClient as any).gesamtleistungData;
    delete (seedClient as any).ulHalbjahr1;
    delete (seedClient as any).ulHalbjahr2;
    delete (seedClient as any).bwaData;
    delete (seedClient as any).unternehmerData;
    delete (seedClient as any).drafts;

    await setDoc(filialRef, {
        clientData: {
            ...defaultClientData,
            ...seedClient,
            branchName: branch.name || '',
            salonname: branch.name || '',
            personalData: [],
            unproductivePersonalData: [],
            azubiAssistenzData: [],
            mitarbeiterLeistung: {},
            gesamtleistungData: [],
            drafts: defaultClientData.drafts,
        },
        migrationMeta: {
            speicherJahr: year,
            branchId: branch.id,
            isBranchAkte: true,
            createdAt: new Date().toISOString(),
            syncedAt: serverTimestamp(),
        },
    }, { merge: true });
};

/** Netto-Monatsumsatz aus BWA/Prognose (gleiche Basis wie calculations.ts) */
export const computeNettoMonatsumsatzFromClientData = (cd: any): number => {
    const parseEuro = (val: any): number => {
        if (val === null || val === undefined) return 0;
        if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
        if (typeof val === 'string') {
            const clean = val.replace(/\./g, '').replace(',', '.');
            const num = parseFloat(clean);
            return isNaN(num) ? 0 : num;
        }
        return 0;
    };
    const bwa = cd?.bwaData || {};
    const zeitraumRaw = parseEuro(bwa.zeitraum);
    const zeitraum = zeitraumRaw > 0 ? zeitraumRaw : 12;
    const bwaUmsatz = parseEuro(bwa.umsatz);
    const prognose = (Array.isArray(bwa.prognoseZeilen) ? bwa.prognoseZeilen : [])
        .filter((z: any) => z && z.aktiv)
        .reduce((sum: number, z: any) => sum + parseEuro(z.jahresumsatz), 0);
    return (bwaUmsatz + prognose) / zeitraum;
};

/**
 * TeamPro+-Schwelle: Netto-Monatsumsatz (Filiale: Summe aller Filialen) vs. Global-Schwelle.
 * Persistiert auf Mandanten-Root inkl. packageTier/tarifStatus (TeamPro ↔ TeamPro+).
 */
export const recomputeAndPersistFilialTeamProPlus = async (
    uid: string,
    year: string,
    rootDataHint?: any,
    settingsGlobal?: Record<string, number> | null
): Promise<{ aggregated: number; isTeamProPlus: boolean } | null> => {
    try {
        const rootRef = doc(db, 'kalkulation_mandanten', uid);
        let rootData = rootDataHint;
        if (!rootData) {
            const snap = await getDoc(rootRef);
            rootData = snap.exists() ? snap.data() : null;
        }
        const meta = resolveBranchMeta(rootData || {});

        let aggregated = 0;
        let mainUmsatz = 0;
        const filialUmsatzById: Record<string, number> = {};

        // Hauptsalon = jahre/{year}
        try {
            const mainSnap = await getDoc(doc(db, 'kalkulation_mandanten', uid, 'jahre', year));
            if (mainSnap.exists()) {
                const mainData = mainSnap.data();
                mainUmsatz = computeNettoMonatsumsatzFromClientData(mainData?.clientData || mainData);
                aggregated += mainUmsatz;
            }
        } catch (_) { /* ignore */ }

        // Nebenfilialen = jahre/{year}/filialen/*
        if (meta.hasBranches) {
            try {
                const filialSnap = await getDocs(collection(db, 'kalkulation_mandanten', uid, 'jahre', year, 'filialen'));
                filialSnap.forEach((d) => {
                    const data = d.data();
                    const u = computeNettoMonatsumsatzFromClientData(data?.clientData || data);
                    filialUmsatzById[d.id] = u;
                    aggregated += u;
                });
            } catch (_) { /* ignore */ }
        }

        // Live-Store ersetzt Cloud-Wert der aktiven Akte
        try {
            const liveCd = useAppStore.getState()?.clientData;
            if (liveCd) {
                const live = computeNettoMonatsumsatzFromClientData(liveCd);
                if (meta.hasBranches) {
                    if (meta.isMain) {
                        aggregated = aggregated - mainUmsatz + live;
                    } else {
                        const cloudFilial = filialUmsatzById[meta.activeBranchId] || 0;
                        aggregated = aggregated - cloudFilial + live;
                    }
                } else {
                    aggregated = live;
                }
                if (aggregated < 0) aggregated = 0;
            }
        } catch (_) { /* ignore */ }

        const g = settingsGlobal
            || useAppStore.getState()?.settings?.global
            || null;
        const { resolvePriceIsSolo, resolveTeamProThreshold } = await import('../utils/clientPricing');
        const isSolo = resolvePriceIsSolo({ client: rootData });
        const threshold = resolveTeamProThreshold(g) ?? 0;

        const tierRaw = String(
            rootData?.packageTier ||
            rootData?.tarifStatus ||
            rootData?.clientData?.packageTier ||
            rootData?.clientData?.tarifStatus ||
            ''
        ).trim();
        const isTeamProLine =
            !isSolo &&
            (
                rootData?.hasEmployeeEval === true ||
                rootData?.clientData?.hasEmployeeEval === true ||
                /^teampro/i.test(tierRaw)
            );
        // Solo/TeamBase bleiben unangetastet; nur TeamPro-Linie → Plus
        const isTeamProPlus = isTeamProLine && threshold > 0 && aggregated >= threshold;
        const nextTier = isTeamProPlus ? 'TeamPro+' : (isTeamProLine ? 'TeamPro' : null);

        await setDoc(rootRef, {
            isTeamProPlus,
            nettoMonatsumsatz: aggregated,
            aggregatedNettoMonatsumsatz: aggregated,
            insiderTeamleaderPlusChecked: isTeamProPlus,
            ...(nextTier
                ? {
                    packageTier: nextTier,
                    tarifStatus: nextTier,
                }
                : {}),
            ...(isTeamProPlus
                ? {
                    insiderTeamleaderStandardChecked: false,
                    hasEmployeeEval: true,
                    isSolo: false,
                    forceSoloTarif: false,
                }
                : {
                    insiderTeamleaderPlusChecked: false,
                }),
            clientData: {
                isTeamProPlus,
                nettoMonatsumsatz: aggregated,
                aggregatedNettoMonatsumsatz: aggregated,
                insiderTeamleaderPlusChecked: isTeamProPlus,
                ...(nextTier
                    ? {
                        packageTier: nextTier,
                        tarifStatus: nextTier,
                    }
                    : {}),
                ...(isTeamProPlus
                    ? {
                        insiderTeamleaderStandardChecked: false,
                        hasEmployeeEval: true,
                        isSolo: false,
                        forceSoloTarif: false,
                    }
                    : {
                        insiderTeamleaderPlusChecked: false,
                    }),
            },
        }, { merge: true });

        // Store ohne Dirty-Loop aktualisieren (setClientData würde Sync retriggern)
        try {
            useAppStore.setState((s: any) => ({
                clientData: {
                    ...(s.clientData || {}),
                    isTeamProPlus,
                    nettoMonatsumsatz: aggregated,
                    aggregatedNettoMonatsumsatz: aggregated,
                    insiderTeamleaderPlusChecked: isTeamProPlus,
                    ...(nextTier
                        ? {
                            packageTier: nextTier,
                            tarifStatus: nextTier,
                        }
                        : {}),
                    ...(isTeamProPlus
                        ? {
                            insiderTeamleaderStandardChecked: false,
                            hasEmployeeEval: true,
                            isSolo: false,
                        }
                        : {}),
                },
            }));
        } catch (_) { /* ignore */ }

        return { aggregated, isTeamProPlus };
    } catch (e: any) {
        console.error('recomputeAndPersistFilialTeamProPlus failed:', e?.message || e);
        return null;
    }
};

const verifyFestsetzungOnServer = async (
    targetUid: string,
    year: string,
    verifyContext: FestsetzungVerifyContext
): Promise<boolean> => {
    let rootData: any = null;
    try {
        const rootSnap = await getDoc(doc(db, 'kalkulation_mandanten', targetUid));
        rootData = rootSnap.exists() ? rootSnap.data() : null;
    } catch (_) { /* ignore */ }
    const yearDocRef = getOperationalYearDocRef(targetUid, year, rootData, useAppStore.getState().clientData);

    for (let attempt = 0; attempt < 6; attempt++) {
        if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
        }

        const serverSnap = await getDocFromServer(yearDocRef);
        if (!serverSnap.exists()) continue;

        const monthSnap = readFestsetzungFromDoc(serverSnap.data(), verifyContext);
        if (hasFestsetzungSnapshot(monthSnap)) {
            return true;
        }
    }

    return false;
};

export const generateMandantenID = (typ: string, nummer: string | number, jahr: string | number, betreuerInitiale: string): string => {
    // Legacy-Signatur beibehalten — Ausgabe im neuen Spacing-Format
    const status = String(typ || '').toUpperCase().includes('B') ? 'TeamBase' : 'TeamPro';
    const initial = String(betreuerInitiale || '').trim().charAt(0).toUpperCase();
    const core = `${nummer}/${jahr}`;
    if (initial) {
        return `MA - ${core} - ${initial} ${status}`;
    }
    return `MA - ${core} - ${status}`;
};

export const getNextMandantenNummer = async (jahr: string): Promise<string> => {
    const counterRef = doc(db, 'system_counters', jahr); 
    try {
        const nextNumber = await runTransaction(db, async (transaction) => {
            const counterDoc = await transaction.get(counterRef);
            if (!counterDoc.exists()) {
                transaction.set(counterRef, { aktuelleNummer: 1 });
                return 1;
            }
            const neueNummer = (counterDoc.data().aktuelleNummer || 0) + 1;
            transaction.update(counterRef, { aktuelleNummer: neueNummer });
            return neueNummer;
        });
        return String(nextNumber).padStart(2, '0');
    } catch (error: any) {
        console.error("🔴 FEHLER BEIM ZÄHLER:", error.message);
        throw new Error("Konnte keine eindeutige Mandant-Nummer generieren.");
    }
};

/** Mitarbeiter mit echtem Namen (keine leere Platzhalter-Zeile). */
export const countNamedStaff = (arr: unknown): number => {
    if (!Array.isArray(arr)) return 0;
    return arr.filter((row: any) => {
        const name = String(row?.name || '').trim();
        if (!name) return false;
        if (/^bitte\s+/i.test(name)) return false;
        return true;
    }).length;
};

const isSparseStaffList = (arr: unknown): boolean => countNamedStaff(arr) === 0;

const isRichMap = (val: unknown): boolean =>
    !!val && typeof val === 'object' && !Array.isArray(val) && Object.keys(val as object).length > 0;

/** History-Spam-Bremse: Voll-Snapshot max. alle 10 Min. — AUSSER Mitarbeiterdaten würden schrumpfen. */
const lastYearPreWriteArchive = new Map<string, number>();

const countAllNamedStaff = (clientData: any): number =>
    countNamedStaff(clientData?.personalData) +
    countNamedStaff(clientData?.unproductivePersonalData) +
    countNamedStaff(clientData?.azubiAssistenzData);

/**
 * VORHER-Snapshot (Undo-Absicherung): Bevor das Jahr-Dokument überschrieben wird,
 * wird der bisherige Server-Stand KOMPLETT in kalkulation_mandanten_history archiviert.
 * Die Root-Schattenkopie (Cloud Function) enthält personalData & Co. nicht — dieser Snapshot schon.
 * Zusammen mit PITR (7 Tage) und täglichen Firestore-Backups ist damit jeder Write rückholbar.
 */
const archiveYearPreWriteSnapshot = async (
    uid: string,
    year: string,
    previousServerClientData: any | null,
    localClientData: any
): Promise<void> => {
    if (!previousServerClientData || typeof previousServerClientData !== 'object') return;

    const staffBefore = countAllNamedStaff(previousServerClientData);
    const staffAfter = countAllNamedStaff(localClientData);
    const staffWouldShrink = staffAfter < staffBefore;

    const key = `${uid}:${year}`;
    const now = Date.now();
    const lastAt = lastYearPreWriteArchive.get(key) || 0;
    if (!staffWouldShrink && now - lastAt < 10 * 60 * 1000) return;

    const baseDoc = {
        originalDocId: uid,
        source: 'year-pre-write',
        speicherJahr: String(year),
        archivedAt: serverTimestamp(),
        staffCountBefore: staffBefore,
        staffCountAfter: staffAfter,
    };
    try {
        await addDoc(collection(db, 'kalkulation_mandanten_history'), {
            ...baseDoc,
            data: { clientData: previousServerClientData },
        });
        lastYearPreWriteArchive.set(key, now);
    } catch (e) {
        // Dokument evtl. > 1 MiB → reduzierter Snapshot mit den kritischen Bereichen
        try {
            await addDoc(collection(db, 'kalkulation_mandanten_history'), {
                ...baseDoc,
                reduced: true,
                data: {
                    clientData: {
                        firmenname: previousServerClientData?.firmenname || '',
                        mandantNr: previousServerClientData?.mandantNr || '',
                        personalData: previousServerClientData?.personalData || [],
                        unproductivePersonalData: previousServerClientData?.unproductivePersonalData || [],
                        azubiAssistenzData: previousServerClientData?.azubiAssistenzData || [],
                        unternehmerData: previousServerClientData?.unternehmerData || {},
                        gesamtleistungData: previousServerClientData?.gesamtleistungData || [],
                        bwaData: previousServerClientData?.bwaData || {},
                    },
                },
            });
            lastYearPreWriteArchive.set(key, now);
        } catch (e2) {
            console.warn('archiveYearPreWriteSnapshot fehlgeschlagen:', e2);
        }
    }
};

/**
 * Bewusste Personal-Löschung erkennen: Nur wenn im aktuellen Tab kürzlich aktiv eine
 * Mitarbeiter-Zeile geändert/gelöscht wurde (Marker aus coreDataSlice) — oder der
 * Einmal-Override PE_FORCE_EMPTY_STAFF_WRITE gesetzt ist — darf „leer über voll" syncen.
 */
const hasRecentIntentionalStaffEdit = (): boolean => {
    try {
        const at = Number((window as any).__peStaffEditAt || 0);
        if (at > 0 && Date.now() - at < 10 * 60 * 1000) return true;
    } catch (_) { /* ignore */ }
    try {
        if (sessionStorage.getItem('PE_FORCE_EMPTY_STAFF_WRITE') === '1') {
            sessionStorage.removeItem('PE_FORCE_EMPTY_STAFF_WRITE');
            return true;
        }
    } catch (_) { /* ignore */ }
    return false;
};

/**
 * Verhindert Gift-Writes: leere lokale Stammdaten dürfen befüllte Cloud-Daten nie überschreiben.
 * (Exakt das Feldstraße-Szenario: Autosync mit Platzhalter-personalData nach God-Mode-Load.)
 */
const protectYearClientDataFromSparsePoison = (
    localClientData: any,
    serverYearClientData: any | null | undefined
): { data: any; blockedKeys: string[] } => {
    if (!serverYearClientData || typeof serverYearClientData !== 'object') {
        return { data: localClientData, blockedKeys: [] };
    }
    const next = localClientData;
    const blockedKeys: string[] = [];

    // Ausnahme nur für BEWUSSTE Löschung: kürzliche Personal-Zeilen-Aktion in diesem Tab
    // (lazy ausgewertet, damit der Einmal-Override nicht unnötig verbraucht wird)
    let intentionalClear: boolean | null = null;
    const allowIntentionalClear = (): boolean => {
        if (intentionalClear === null) intentionalClear = hasRecentIntentionalStaffEdit();
        return intentionalClear;
    };

    const staffKeys = ['personalData', 'unproductivePersonalData', 'azubiAssistenzData'] as const;
    for (const key of staffKeys) {
        const localArr = next?.[key];
        const serverArr = serverYearClientData?.[key];
        if (isSparseStaffList(localArr) && countNamedStaff(serverArr) > 0) {
            if (allowIntentionalClear()) {
                console.warn(`[Sync-Gift-Schutz] ${key}: Leerung zugelassen — bewusste Personal-Aktion in diesem Tab`);
                continue;
            }
            next[key] = serverArr;
            blockedKeys.push(key);
        }
    }

    // Leistung / BWA / Unternehmer: lokal leer, Server voll → Server behalten
    if (!isRichMap(next?.mitarbeiterLeistung) && isRichMap(serverYearClientData?.mitarbeiterLeistung)) {
        next.mitarbeiterLeistung = serverYearClientData.mitarbeiterLeistung;
        blockedKeys.push('mitarbeiterLeistung');
    }
    if (!isRichMap(next?.bwaData) && isRichMap(serverYearClientData?.bwaData)) {
        next.bwaData = serverYearClientData.bwaData;
        blockedKeys.push('bwaData');
    }
    if (!isRichMap(next?.unternehmerData) && isRichMap(serverYearClientData?.unternehmerData)) {
        next.unternehmerData = serverYearClientData.unternehmerData;
        blockedKeys.push('unternehmerData');
    }
    if (
        (!Array.isArray(next?.gesamtleistungData) || next.gesamtleistungData.length === 0) &&
        Array.isArray(serverYearClientData?.gesamtleistungData) &&
        serverYearClientData.gesamtleistungData.length > 0
    ) {
        next.gesamtleistungData = serverYearClientData.gesamtleistungData;
        blockedKeys.push('gesamtleistungData');
    }

    return { data: next, blockedKeys };
};

export const migrateLocalStateToCloud = async (forceGlobal: boolean = false): Promise<boolean> => {
    const writeEpoch = getSyncEpoch();
    /** true sobald Jahresakte geschrieben — Root-Folgefehler dürfen dann keinen FEHLER-Badge auslösen */
    let yearWriteOk = false;
    try {
        const user = auth.currentUser;
        if (!user) return false;

        // Unvollständiger God-Mode-/Load-Fallback: nie in die Cloud schreiben.
        // Ausnahme forceGlobal: explizite Aktionen (Onboarding-Seed, Admin-Restore) —
        // die schreiben bewusst und werden zusätzlich vom Gift-Schutz abgesichert.
        // Autosync nur wenn: isInitialized && !cloudWriteLock && !BLOCKED && cloudLoadOk.
        // Gott-Modus mit bereits geladener echter Akte: LOAD_OK darf nicht dauerhaft blockieren
        // (z. B. nach Tab-Fokus / Soft-Reset — Coach muss weiter in die Cloud schreiben können).
        const godModeIdEarly = useAdminStore.getState().impersonatedClientId
            || (() => { try { return sessionStorage.getItem('GOD_MODE_ID'); } catch { return null; } })();
        try {
            if (!forceGlobal && sessionStorage.getItem('PE_CLOUD_SYNC_BLOCKED') === '1') {
                // Gott-Modus + initialisierte echte Akte: Block lösen und weiter speichern
                const st = useAppStore.getState();
                const cd = st?.clientData;
                const looksLoaded = !!(
                    godModeIdEarly
                    && st?.isInitialized
                    && cd
                    && String(cd.firmenname || '') !== 'Laden fehlgeschlagen'
                    && String(cd.mandantNr || '').trim()
                    && String(cd.mandantNr || '').trim() !== 'GOD-MODE'
                );
                if (looksLoaded) {
                    try {
                        sessionStorage.removeItem('PE_CLOUD_SYNC_BLOCKED');
                        sessionStorage.setItem('PE_CLOUD_LOAD_OK', '1');
                    } catch (_) { /* ignore */ }
                    if (st.cloudWriteLock) {
                        try { useAppStore.setState({ cloudWriteLock: false }); } catch (_) { /* ignore */ }
                    }
                } else {
                    console.warn('migrateLocalStateToCloud: abgebrochen — PE_CLOUD_SYNC_BLOCKED (unvollständiger Load)');
                    return false;
                }
            }
            if (!forceGlobal && sessionStorage.getItem('PE_CLOUD_LOAD_OK') !== '1') {
                const st = useAppStore.getState();
                const cd = st?.clientData;
                const looksLoaded = !!(
                    godModeIdEarly
                    && st?.isInitialized
                    && cd
                    && String(cd.firmenname || '') !== 'Laden fehlgeschlagen'
                    && String(cd.mandantNr || '').trim()
                    && String(cd.mandantNr || '').trim() !== 'GOD-MODE'
                );
                if (looksLoaded) {
                    try { sessionStorage.setItem('PE_CLOUD_LOAD_OK', '1'); } catch (_) { /* ignore */ }
                    if (st.cloudWriteLock) {
                        try { useAppStore.setState({ cloudWriteLock: false }); } catch (_) { /* ignore */ }
                    }
                } else {
                    console.warn('migrateLocalStateToCloud: abgebrochen — PE_CLOUD_LOAD_OK fehlt (Boot/Timeout)');
                    return false;
                }
            }
        } catch (_) { /* ignore */ }

        if (writeEpoch !== getSyncEpoch()) {
            console.warn('migrateLocalStateToCloud: abgebrochen — syncEpoch Mismatch (Kontextwechsel)');
            return false;
        }

        // God-Mode-UID: Store ODER sessionStorage — sonst schreibt Coach auf die eigene UID
        const godModeId =
            useAdminStore.getState().impersonatedClientId
            || (() => { try { return sessionStorage.getItem('GOD_MODE_ID'); } catch { return null; } })();
        const targetUid = godModeId || user.uid;
        const inGodMode = !!godModeId;

        if (await rejectIfGodModeLockLive(targetUid, inGodMode, 'migrateLocalStateToCloud')) {
            return false;
        }

        // Zombie-Killer: vor JEDEM Write Session prüfen (Option A: God-Mode übersprungen).
        // Fehlt/ abgelaufen die Session → stiller Reclaim in assertLocalSessionOwnsWrite.
        const ownsSession = await assertLocalSessionOwnsWrite(targetUid, {
            skipForGodMode: inGodMode,
            userId: user.uid,
            claimantIsTeam: inGodMode,
        });
        if (!ownsSession) {
            // Kein setSessionBlocked hier: Dashboard ohne Claim ist legitim; Akte-Block kommt aus Live-Guard/Claim
            console.warn('migrateLocalStateToCloud: Schreibvorgang abgebrochen — keine eigene Session');
            return false;
        }

        // Admin-Overwrite-Schutz: neuerer lastAdminUpdate auf Server → Re-Fetch, kein Client-Write
        const mayWrite = await guardClientWriteAgainstAdminUpdate(targetUid, inGodMode);
        if (!mayWrite) {
            return false;
        }

        if (writeEpoch !== getSyncEpoch()) {
            console.warn('migrateLocalStateToCloud: abgebrochen — syncEpoch Mismatch vor State-Read');
            return false;
        }

        const state = useAppStore.getState();
        
        if (!state || !state.clientData) {
            return false;
        }

        if (!forceGlobal && !state.isInitialized) {
            console.warn('migrateLocalStateToCloud: abgebrochen — Store nicht initialisiert');
            return false;
        }

        if (state.cloudWriteLock && !forceGlobal) {
            return false;
        }

        const checkData = state.clientData;
        const mandantNrCheck = String(checkData.mandantNr || '').trim();
        if (mandantNrCheck === 'GOD-MODE' || /^god-?mode$/i.test(mandantNrCheck)) {
            console.warn('migrateLocalStateToCloud: abgebrochen — GOD-MODE-Platzhalter-MandantNr');
            return false;
        }
        const hasNoMandantNr = !checkData.mandantNr || checkData.mandantNr === "WIEDERHERGESTELLT" || checkData.mandantNr === "";
        const hasNoName = !checkData.firmenname || checkData.firmenname === "Bitte Namen eintragen" || checkData.firmenname === "";
        
        if (!forceGlobal && hasNoMandantNr && hasNoName) {
            return false;
        }
        
        let aktuellesJahr = getActiveGeschaeftsjahr(checkData);

        let cleanClientData;
        try {
            cleanClientData = JSON.parse(JSON.stringify(state.clientData));
        } catch (parseError) {
            return false;
        }
        
        delete (cleanClientData as any).settings;
        delete (cleanClientData as any).team;
        delete (cleanClientData as any).appSettings;

        cleanClientData = sanitizeClientData(cleanClientData);
        const festsetzungsSnapshots = buildFestsetzungsSnapshotsFromClientData(cleanClientData);
        cleanClientData = serializeClientDataForCloud(cleanClientData);

        // Admin-/Lifecycle-Flags: Client-Sync darf diese NIE überschreiben.
        // Abrechnungsflags gehören ausschließlich dem Admin (Stammdaten-Drawer) —
        // sonst überschreibt ein Mandanten-Sync mit veraltetem Store z. B. den
        // manuell erzwungenen Neukunden-Aufschlag.
        const ADMIN_LOCK_KEYS = [
            'isUnlockedByAdmin',
            'isReadyForReview',
            'isActive',
            'isDeleted',
            'onboardingStatus',
            'forceNewCustomerPremium',
            'disableNewCustomerPremium',
            'neukundenManualOverride',
            'isPremium2027',
            'isBestandskunde',
            'vertragsbeginn',
            'hasEmployeeEval',
            'isSolo',
            'isExistingCustomer',
            'existingCustomerNumber',
            'customerNumber',
            'billingStatus',
            'packageTier',
            'tarifStatus',
            'sepaCancelPending',
            'billingActivatedAt',
            'stripeSubscriptionId',
            'monthlyFee',
            'proBonoActive',
            'proBonoChecked',
            'proBonoUntil',
            'proBonoEndDate',
            'isLegacy',
            'stripeStatus',
            'stripeSetupComplete',
            'stripeCustomerId',
            'stripePaymentLockBypass',
            'stripeRadarMuted',
            'stripeRadarSnoozedUntil',
            'stripeRadarLastAction',
            'stripeRadarLastActionAt',
            'stripeRadarLastActionByEmail',
            'stripeRadarLastActionByName',
            'stripeRadarLastActionByUid',
            // Sentinel Radar: Admin-only — sonst schreibt Jahr-/Store-Altflag Mute wieder zurück
            'sentinelMuted',
            'sentinelSnoozedUntil',
            'sentinelLastAction',
            'sentinelLastActionAt',
            'sentinelLastActionByEmail',
            'sentinelLastActionByName',
            'sentinelLastActionByUid',
            'sentinelLastActionDays',
        ] as const;
        for (const key of ADMIN_LOCK_KEYS) {
            delete (cleanClientData as any)[key];
        }

        // Platzhalter-Mandanten-Nr. aus Store NIEMALS in die Cloud schreiben
        const storeNrRaw = String(cleanClientData.mandantNr || '').trim();
        if (
            !storeNrRaw ||
            storeNrRaw === 'WIEDERHERGESTELLT' ||
            /^bitte\s+namen/i.test(storeNrRaw)
        ) {
            delete (cleanClientData as any).mandantNr;
        }

        const yearPayload = {
            clientData: cleanClientData, 
            festsetzungsSnapshots,
            savedSnapshots: state.savedSnapshots || [],
            migrationMeta: {
                syncedAt: serverTimestamp(),
                originalMandantNr: state.clientData?.mandantNr || "Unbekannt",
                isCloudActive: true,
                speicherJahr: aktuellesJahr
            }
        };

        // Root kurz lesen für Filial-Meta (ohne Registry zu zerstören)
        let rootSnapData: any = null;
        try {
            const rootSnap = await getDoc(doc(db, 'kalkulation_mandanten', targetUid));
            rootSnapData = rootSnap.exists() ? rootSnap.data() : null;
        } catch (_) { /* ignore */ }

        const branchMeta = resolveBranchMeta(rootSnapData || {}, state.clientData);
        const userDocRef = getOperationalYearDocRef(targetUid, aktuellesJahr, rootSnapData, state.clientData);

        // Gift-Schutz: Server-Jahr lesen und leere lokale Arrays nicht drüberbügeln
        let serverYearClientData: any = null;
        try {
            const yearSnap = await getDocFromServer(userDocRef);
            if (yearSnap.exists()) {
                serverYearClientData = yearSnap.data()?.clientData || null;
            }
        } catch (_) {
            try {
                const yearSnap = await getDoc(userDocRef);
                if (yearSnap.exists()) {
                    serverYearClientData = yearSnap.data()?.clientData || null;
                }
            } catch (_) { /* ignore */ }
        }
        const protectedPack = protectYearClientDataFromSparsePoison(cleanClientData, serverYearClientData);
        cleanClientData = protectedPack.data;
        if (protectedPack.blockedKeys.length > 0) {
            console.error(
                '[Sync-Gift-Schutz] Leere lokale Daten hätten Cloud überschrieben — Server behalten:',
                protectedPack.blockedKeys.join(', '),
                `| uid=${targetUid} jahr=${aktuellesJahr}`
            );
            yearPayload.clientData = cleanClientData;
            // Store sofort heilen, damit UI nicht „leer“ bleibt und gleich wieder giftet
            try {
                const patch: Record<string, any> = {};
                for (const key of protectedPack.blockedKeys) {
                    patch[key] = cleanClientData[key];
                }
                const prev = useAppStore.getState().clientData || {};
                useAppStore.setState({ clientData: { ...prev, ...patch } as any });
            } catch (_) { /* ignore */ }
        }

        // VORHER-Stand archivieren, DANN erst überschreiben — jeder Write bleibt rückholbar
        if (writeEpoch !== getSyncEpoch()) {
            console.warn('migrateLocalStateToCloud: abgebrochen — syncEpoch Mismatch vor Year-Write');
            return false;
        }
        await archiveYearPreWriteSnapshot(targetUid, aktuellesJahr, serverYearClientData, cleanClientData);

        if (writeEpoch !== getSyncEpoch()) {
            console.warn('migrateLocalStateToCloud: abgebrochen — syncEpoch Mismatch vor Year-setDoc');
            return false;
        }
        await setDoc(userDocRef, {
            ...yearPayload,
            migrationMeta: {
                ...yearPayload.migrationMeta,
                branchId: branchMeta.activeBranchId,
                isBranchAkte: !branchMeta.isMain,
            },
        }, { merge: true });
        // Jahresakte ist SoT — Seite zu/auf ist hier bereits persistiert.
        yearWriteOk = true;

        const rootDocRef = doc(db, 'kalkulation_mandanten', targetUid);

        // Nebenfiliale: Root-clientData (Hauptsalon-Stammdaten) NICHT mit Filial-Ops überschreiben
        if (!branchMeta.isMain) {
            if (writeEpoch !== getSyncEpoch()) return true;
            try {
                await setDoc(rootDocRef, {
                    aktivesJahr: aktuellesJahr,
                    activeBranchId: branchMeta.activeBranchId,
                    hasBranches: true,
                    branches: branchMeta.branches,
                    'clientData.activeBranchId': branchMeta.activeBranchId,
                    'clientData.hasBranches': true,
                }, { merge: true });
                await purgeLegacyRootOpsData(targetUid);
                if (branchMeta.hasBranches) {
                    await recomputeAndPersistFilialTeamProPlus(
                        targetUid,
                        aktuellesJahr,
                        { ...(rootSnapData || {}), hasBranches: true, branches: branchMeta.branches },
                        state.settings?.global
                    );
                }
            } catch (rootErr: any) {
                console.warn('migrateLocalStateToCloud: Root-Nachzug (Filiale) fehlgeschlagen — Year ok:', rootErr?.message || rootErr);
            }
            return true;
        }

        const minimalRootClientData = { ...cleanClientData };
        for (const key of ROOT_OPS_STRIP_KEYS) {
            delete (minimalRootClientData as any)[key];
        }

        // Registry am Root halten
        if (branchMeta.hasBranches) {
            minimalRootClientData.hasBranches = true;
            minimalRootClientData.branches = branchMeta.branches;
            minimalRootClientData.activeBranchId = branchMeta.activeBranchId;
        }

        // Registrierungs-/Abrechnungsfelder in clientData NIE mit Store-Leere überschreiben
        // (setDoc merge ersetzt die ganze clientData-Map!)
        const rootCd = (rootSnapData?.clientData || {}) as Record<string, any>;
        const PRESERVE_CD_KEYS = [
            'tarifStatus',
            'packageTier',
            'customerNumber',
            'billingStatus',
            'vertragsbeginn',
            'hasEmployeeEval',
            'isSolo',
            'isExistingCustomer',
            'isBestandskunde',
            'existingCustomerNumber',
            'sepaCancelPending',
            'mandantNr',
            'providedOldId',
        ] as const;
        const isPoison = (v: unknown) =>
            v === undefined ||
            v === null ||
            v === '' ||
            v === 'WIEDERHERGESTELLT' ||
            (typeof v === 'string' && /^bitte\s+namen/i.test(v));
        for (const key of PRESERVE_CD_KEYS) {
            const storeVal = (minimalRootClientData as any)[key];
            const nestedVal = rootCd[key];
            const rootVal = (rootSnapData as any)?.[key];
            if (isPoison(storeVal)) {
                if (!isPoison(nestedVal)) (minimalRootClientData as any)[key] = nestedVal;
                else if (!isPoison(rootVal)) (minimalRootClientData as any)[key] = rootVal;
                else delete (minimalRootClientData as any)[key];
            }
        }

        if (writeEpoch !== getSyncEpoch()) {
            console.warn('migrateLocalStateToCloud: syncEpoch Mismatch vor Root-Write — Year bereits gespeichert');
            return true;
        }
        try {
            await setDoc(rootDocRef, {
                clientData: minimalRootClientData, 
                unlockedPremiumTools: Array.isArray(state.unlockedPremiumTools)
                    ? state.unlockedPremiumTools
                    : (Array.isArray(minimalRootClientData.unlockedPremiumTools)
                        ? minimalRootClientData.unlockedPremiumTools
                        : []),
                // Master-PIN: Root-Top-Level (überlebt Jahreswechsel); Store > Root-Alt > clientData
                pinLockConfig: (() => {
                    const fromStore = (state.clientData as any)?.pinLockConfig;
                    if (fromStore && typeof fromStore === 'object') return fromStore;
                    if (rootSnapData?.pinLockConfig && typeof rootSnapData.pinLockConfig === 'object') {
                        return rootSnapData.pinLockConfig;
                    }
                    const fromCd = (minimalRootClientData as any)?.pinLockConfig;
                    return fromCd && typeof fromCd === 'object' ? fromCd : {
                        isPinActive: false,
                        pinHash: '',
                        lockedPageIds: [],
                    };
                })(),
                // Platzhalter (WIEDERHERGESTELLT) darf echte Registrierungs-Nr. nie überschreiben
                ...(() => {
                    const storeNr = String(state.clientData?.mandantNr || '').trim();
                    const rootNr = String(rootSnapData?.mandantNr || '').trim();
                    const isPlaceholder = (n: string) =>
                        !n || n === 'WIEDERHERGESTELLT' || /^bitte\s+namen/i.test(n);
                    if (!isPlaceholder(storeNr)) return { mandantNr: storeNr };
                    if (!isPlaceholder(rootNr)) return {}; // Root behalten
                    return {}; // nichts schreiben
                })(),
                betreuer: state.clientData?.betreuer || '',
                land: state.clientData?.land || 'DE',
                aktivesJahr: aktuellesJahr,
                ...(branchMeta.hasBranches ? {
                    hasBranches: true,
                    branches: branchMeta.branches,
                    activeBranchId: branchMeta.activeBranchId,
                } : {}),
            }, { merge: true });

            // Vektor 1: physisches deleteField der Ops-Altlasten am Root (Stammdaten unberührt)
            await purgeLegacyRootOpsData(targetUid);

            if (writeEpoch === getSyncEpoch()) {
                if (branchMeta.hasBranches) {
                    await recomputeAndPersistFilialTeamProPlus(
                        targetUid,
                        aktuellesJahr,
                        {
                            ...(rootSnapData || {}),
                            hasBranches: true,
                            branches: branchMeta.branches,
                            isSolo: state.clientData?.isSolo,
                            forceSoloTarif: state.clientData?.forceSoloTarif,
                            hasEmployeeEval: state.clientData?.hasEmployeeEval,
                            clientData: minimalRootClientData,
                        },
                        state.settings?.global
                    );
                } else {
                    await recomputeAndPersistFilialTeamProPlus(
                        targetUid,
                        aktuellesJahr,
                        {
                            ...(rootSnapData || {}),
                            isSolo: state.clientData?.isSolo,
                            forceSoloTarif: state.clientData?.forceSoloTarif,
                            hasEmployeeEval: state.clientData?.hasEmployeeEval,
                            packageTier: rootSnapData?.packageTier || minimalRootClientData?.packageTier,
                            tarifStatus: rootSnapData?.tarifStatus || minimalRootClientData?.tarifStatus,
                            clientData: minimalRootClientData,
                        },
                        state.settings?.global
                    );
                }
            }
        } catch (rootErr: any) {
            console.warn('migrateLocalStateToCloud: Root-Nachzug fehlgeschlagen — Year ok:', rootErr?.message || rootErr);
        }

        return true;
    } catch (error: any) {
        console.error("🔴 FEHLER BEI MIGRATION:", error.message);
        // Year schon geschrieben → Daten sind drin, Badge nicht rot machen
        if (yearWriteOk) {
            console.warn('migrateLocalStateToCloud: Folgefehler nach Year-Write — Sync als Erfolg');
            return true;
        }
        return false;
    }
};

let activeImmediateUpload: Promise<boolean> | null = null;

export const persistClientDataImmediate = async (
    payload?: FestsetzungPersistPayload
): Promise<boolean> => {
    const runPersist = async (): Promise<boolean> => {
        const user = auth.currentUser;
        if (!user) return false;

        const godModeId =
            useAdminStore.getState().impersonatedClientId
            || (() => { try { return sessionStorage.getItem('GOD_MODE_ID'); } catch { return null; } })();
        const targetUid = godModeId || user.uid;
        const inGodMode = !!godModeId;

        if (await rejectIfGodModeLockLive(targetUid, inGodMode, 'persistClientDataImmediate')) {
            return false;
        }

        const ownsSession = await assertLocalSessionOwnsWrite(targetUid, {
            skipForGodMode: inGodMode,
            userId: user.uid,
            claimantIsTeam: inGodMode,
        });
        if (!ownsSession) {
            console.warn('persistClientDataImmediate: Schreibvorgang abgebrochen — keine eigene Session');
            return false;
        }

        const mayWrite = await guardClientWriteAgainstAdminUpdate(targetUid, inGodMode);
        if (!mayWrite) {
            return false;
        }

        if (payload?.verifyContext) {
            if (payload.isLocked && payload.snapshot) {
                const dedicatedWrite = await writeFestsetzungToDedicatedField(
                    payload.verifyContext,
                    payload.snapshot,
                    { lockedAt: payload.lockedAt, lockedBy: payload.lockedBy }
                );
                if (!dedicatedWrite) {
                    return false;
                }
            } else if (!payload.isLocked) {
                await deleteFestsetzungFromDedicatedField(payload.verifyContext);
            }
        }

        const success = await migrateLocalStateToCloud(true);
        if (!success) return false;

        if (!payload?.verifyContext || !payload.isLocked) return true;

        const year = getActiveGeschaeftsjahr();
        const verified = await verifyFestsetzungOnServer(targetUid, year, payload.verifyContext);

        if (!verified) {
            console.error('persistClientDataImmediate: Server verification failed for festsetzung', payload.verifyContext);
        }

        return verified;
    };

    if (activeImmediateUpload) {
        return activeImmediateUpload;
    }

    activeImmediateUpload = runPersist().finally(() => {
        activeImmediateUpload = null;
    });

    return activeImmediateUpload;
};

export const waitForCloudWriteLock = async (timeoutMs = 15000): Promise<void> => {
    const start = Date.now();
    while (useAppStore.getState().cloudWriteLock) {
        if (Date.now() - start > timeoutMs) {
            console.warn('waitForCloudWriteLock: timeout while waiting for snapshot upload');
            break;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
};

/** Erster definierter Wert — Reihenfolge = Vorrang (Root vor clientData-Kopien). */
const pickDefinedFlag = (...vals: any[]) => vals.find((v) => v !== undefined && v !== null);
const billingFlagTrue = (v: any): boolean => v === true || v === 1 || v === '1' || v === 'true';

/**
 * Year-SoT (Map): Jahresobjekt vorhanden → 100 % Jahr (+ Defaults nur für Struktur-Keys).
 * KEIN Root-Overlay — "" / 0 / fehlende Keys im Jahr bleiben Jahr (Defaults füllen nur Struktur).
 * Root nur, wenn für dieses Jahr noch kein Datensatz existiert.
 */
const pickYearMapSoT = (yearVal: any, rootVal: any, defaults: any) => {
    if (yearVal != null && typeof yearVal === 'object' && !Array.isArray(yearVal)) {
        return { ...(defaults || {}), ...yearVal };
    }
    if (rootVal != null && typeof rootVal === 'object' && !Array.isArray(rootVal)) {
        return { ...(defaults || {}), ...rootVal };
    }
    return { ...(defaults || {}) };
};

/**
 * Year-SoT (Array): Jahres-Array vorhanden (auch []) → 100 % Jahr.
 * Root nur, wenn das Jahr den Key noch nicht trägt.
 */
const pickYearArraySoT = (yearVal: any, rootVal: any, defaults: any[] = []) => {
    if (Array.isArray(yearVal)) return yearVal;
    if (Array.isArray(rootVal)) return rootVal;
    return defaults;
};

export const loadCloudStateToLocal = async (uid: string, zielJahr?: string): Promise<any | null> => {
    let rootData: any = null;
    let sysData: any = null;
    let yearData: any = null;
    let rootFromServer = false;
    let yearFromServer = false;
    let yearDocExisted = false;

    // Vektor 3: Root explizit vom Server — kein IndexedDB-Cache-Hit als Load-OK
    try {
        const rootDocRef = doc(db, 'kalkulation_mandanten', uid);
        const rootDoc = await getDocFromServer(rootDocRef);
        rootData = rootDoc.exists() ? rootDoc.data() : null;
        rootFromServer = true;
        if (rootData) {
            rememberLastAdminUpdate(uid, rootData.lastAdminUpdate);
        }
    } catch (e: any) {
        console.warn('loadCloudStateToLocal: Root getDocFromServer fehlgeschlagen — Load fail-closed', e);
        return null;
    }

    try {
        let sysRef = doc(db, 'system_data', 'global_settings');
        let sysDoc = await getDoc(sysRef);
        if (!sysDoc.exists()) {
            sysRef = doc(db, 'system_data', 'master');
            sysDoc = await getDoc(sysRef);
        }
        sysData = sysDoc.exists() ? sysDoc.data() : null;
    } catch (e: any) {}

    let abrufJahr = zielJahr || new Date().getFullYear().toString();
    
    const sessionYear = sessionStorage.getItem('CURRENT_YEAR');
    const localYear = localStorage.getItem('CURRENT_YEAR');
    
    if (sessionYear) {
        abrufJahr = sessionYear;
    } else if (localYear) {
        abrufJahr = localYear;
    } else if (rootData?.aktivesJahr) {
        abrufJahr = rootData.aktivesJahr.toString();
    } else if (rootData?.geschaeftsjahre && rootData.geschaeftsjahre.length > 0) {
        const sortedYears = [...rootData.geschaeftsjahre].sort((a, b) => Number(b.jahr) - Number(a.jahr));
        abrufJahr = sortedYears[0].jahr.toString();
    }

    // Vektor 3: Jahresdokument explizit vom Server
    try {
        const yearDocRef = getOperationalYearDocRef(uid, abrufJahr, rootData);
        const yearDoc = await getDocFromServer(yearDocRef);
        yearDocExisted = yearDoc.exists();
        yearData = yearDocExisted ? yearDoc.data() : null;
        yearFromServer = true;

        // Nebenfiliale ohne Doc: leere Ops-Hülle (Identität kommt vom Root)
        const branchMetaEarly = resolveBranchMeta(rootData || {});
        if (!yearData && !branchMetaEarly.isMain) {
            yearData = { clientData: { ...defaultClientData }, savedSnapshots: [] };
        }
    } catch (e: any) {
        console.warn('loadCloudStateToLocal: Year getDocFromServer fehlgeschlagen — Load fail-closed', e);
        return null;
    }

    if (!rootFromServer || !yearFromServer) {
        console.warn('loadCloudStateToLocal: Server-Verifikation unvollständig — kein LOAD_OK');
        return null;
    }

    if (!rootData && !yearData) {
        if (sysData) {
            return {
                clientData: defaultClientData,
                settings: mergeWithDefaultSettings(sysData?.settings || null),
                team: sysData?.team || [],
                appSettings: sysData?.appSettings || {},
                savedSnapshots: [],
                serverVerified: true,
            };
        }
        return null;
    }

    const rootClientData = rootData?.clientData || {};
    const yearClientData = yearData?.clientData || {};

    const rawLand = rootData?.land || rootData?.Land || rootClientData?.land || rootClientData?.Land || yearData?.land || yearData?.Land || 'DE';
    const fetchedLand = String(rawLand).toUpperCase();

    const isUnlocked =
        rootData?.isUnlockedByAdmin === true ||
        rootClientData?.isUnlockedByAdmin === true ||
        yearData?.isUnlockedByAdmin === true ||
        yearClientData?.isUnlockedByAdmin === true;


    const fetchedMandantNr = rootData?.mandantNr || rootClientData?.mandantNr || yearData?.mandantNr || yearClientData?.mandantNr || '';
    const fetchedBetreuer = rootData?.betreuer || rootClientData?.betreuer || yearData?.betreuer || yearClientData?.betreuer || '';

    const fetchedVorname = rootData?.vorname || rootClientData?.vorname || yearData?.vorname || yearClientData?.vorname || '';
    const fetchedNachname = rootData?.nachname || rootClientData?.nachname || yearData?.nachname || yearClientData?.nachname || '';
    const fetchedFirmenname = rootData?.firmenname || rootData?.salonname || rootClientData?.firmenname || rootClientData?.salonname || '';
    const fetchedEmail = rootData?.email || rootClientData?.email || rootClientData?.mailadresse || '';
    
    const fetchedGeschaeftsjahre = rootData?.geschaeftsjahre || rootClientData?.geschaeftsjahre || yearData?.geschaeftsjahre || [];

    const forcedLizenzTyp = rootData?.lizenzDetails?.typ || rootClientData?.lizenzDetails?.typ || (rootClientData?.basicChecked ? 'basic' : 'insider');
    const forcedBasicChecked = rootClientData?.basicChecked ?? (forcedLizenzTyp === 'basic');
    const forcedInsiderChecked = rootClientData?.insiderTeamleaderStandardChecked ?? (forcedLizenzTyp === 'insider');

    // Year-SoT: Operative Blöcke ausschließlich aus dem Jahresdokument,
    // sobald der jeweilige Block im Jahr existiert. Root füllt NUR, wenn das Jahr
    // den Block noch nicht trägt. "" / 0 / [] im Jahr sind valide — kein Root-Fallback.
    let mergedClientData = {
        ...defaultClientData,
        ...rootClientData,
        ...yearClientData,
        unternehmerData: pickYearMapSoT(
            yearClientData?.unternehmerData,
            rootClientData?.unternehmerData,
            defaultClientData.unternehmerData
        ),
        bwaData: pickYearMapSoT(
            yearClientData?.bwaData,
            rootClientData?.bwaData,
            defaultClientData.bwaData
        ),
        kalkulator: pickYearMapSoT(
            yearClientData?.kalkulator,
            rootClientData?.kalkulator,
            defaultClientData.kalkulator
        ),
        lohnfaktorData: pickYearMapSoT(
            yearClientData?.lohnfaktorData,
            rootClientData?.lohnfaktorData,
            defaultClientData.lohnfaktorData
        ),
        mitarbeiterLeistung: pickYearMapSoT(
            yearClientData?.mitarbeiterLeistung,
            rootClientData?.mitarbeiterLeistung,
            defaultClientData.mitarbeiterLeistung
        ),
        ulBasisdaten: pickYearMapSoT(
            yearClientData?.ulBasisdaten,
            rootClientData?.ulBasisdaten,
            defaultClientData.ulBasisdaten
        ),
        cachedUnternehmerMetrics: pickYearMapSoT(
            yearClientData?.cachedUnternehmerMetrics,
            rootClientData?.cachedUnternehmerMetrics,
            defaultClientData.cachedUnternehmerMetrics
        ),
        gesamtleistungSettings: pickYearMapSoT(
            yearClientData?.gesamtleistungSettings,
            rootClientData?.gesamtleistungSettings,
            defaultClientData.gesamtleistungSettings
        ),
        personalData: pickYearArraySoT(
            yearClientData?.personalData,
            rootClientData?.personalData,
            defaultClientData.personalData
        ),
        unproductivePersonalData: pickYearArraySoT(
            yearClientData?.unproductivePersonalData,
            rootClientData?.unproductivePersonalData,
            defaultClientData.unproductivePersonalData
        ),
        azubiAssistenzData: pickYearArraySoT(
            yearClientData?.azubiAssistenzData,
            rootClientData?.azubiAssistenzData,
            defaultClientData.azubiAssistenzData
        ),
        gesamtleistungData: pickYearArraySoT(
            yearClientData?.gesamtleistungData,
            rootClientData?.gesamtleistungData,
            defaultClientData.gesamtleistungData
        ),
        ulHalbjahr1: pickYearArraySoT(
            yearClientData?.ulHalbjahr1,
            rootClientData?.ulHalbjahr1,
            defaultClientData.ulHalbjahr1
        ),
        ulHalbjahr2: pickYearArraySoT(
            yearClientData?.ulHalbjahr2,
            rootClientData?.ulHalbjahr2,
            defaultClientData.ulHalbjahr2
        ),
        land: fetchedLand,
        forceSoloTarif: rootData?.forceSoloTarif === true || rootClientData?.forceSoloTarif === true || yearClientData?.forceSoloTarif === true,
        isSolo: rootData?.isSolo === true || rootClientData?.isSolo === true || yearClientData?.isSolo === true
            ? true
            : (rootData?.isSolo === false || rootClientData?.isSolo === false || yearClientData?.isSolo === false
                ? false
                : (rootClientData?.isSolo ?? yearClientData?.isSolo)),
        hasEmployeeEval: rootData?.hasEmployeeEval === true || rootClientData?.hasEmployeeEval === true || yearClientData?.hasEmployeeEval === true
            ? true
            : (rootData?.hasEmployeeEval === false || rootClientData?.hasEmployeeEval === false || yearClientData?.hasEmployeeEval === false
                ? false
                : (rootClientData?.hasEmployeeEval ?? yearClientData?.hasEmployeeEval)),
        // Abrechnungsflags: Root-Ebene (Admin-Quelle) hat VORRANG vor eingebetteten
        // clientData-Kopien — die können vom Mandanten-Sync veraltet sein.
        forceNewCustomerPremium: billingFlagTrue(
            pickDefinedFlag(
                rootData?.forceNewCustomerPremium,
                rootClientData?.forceNewCustomerPremium,
                yearClientData?.forceNewCustomerPremium
            )
        ),
        disableNewCustomerPremium:
            !billingFlagTrue(
                pickDefinedFlag(
                    rootData?.forceNewCustomerPremium,
                    rootClientData?.forceNewCustomerPremium,
                    yearClientData?.forceNewCustomerPremium
                )
            ) &&
            billingFlagTrue(
                pickDefinedFlag(
                    rootData?.disableNewCustomerPremium,
                    rootClientData?.disableNewCustomerPremium,
                    yearClientData?.disableNewCustomerPremium
                )
            ),
        neukundenManualOverride: billingFlagTrue(
            pickDefinedFlag(
                rootData?.neukundenManualOverride,
                rootClientData?.neukundenManualOverride,
                yearClientData?.neukundenManualOverride
            )
        ),
        stripeStatus:
            rootData?.stripeStatus ??
            rootClientData?.stripeStatus ??
            yearClientData?.stripeStatus ??
            null,
        stripeSetupComplete: billingFlagTrue(
            pickDefinedFlag(
                rootData?.stripeSetupComplete,
                rootClientData?.stripeSetupComplete,
                yearClientData?.stripeSetupComplete
            )
        ),
        stripeCustomerId:
            rootData?.stripeCustomerId ||
            rootClientData?.stripeCustomerId ||
            yearClientData?.stripeCustomerId ||
            rootData?.stripeCustomer ||
            rootClientData?.stripeCustomer ||
            yearClientData?.stripeCustomer ||
            '',
        stripePaymentLockBypass: billingFlagTrue(
            pickDefinedFlag(
                rootData?.stripePaymentLockBypass,
                rootClientData?.stripePaymentLockBypass,
                yearClientData?.stripePaymentLockBypass
            )
        ),
        isPremium2027:
            billingFlagTrue(
                pickDefinedFlag(
                    rootData?.isPremium2027,
                    rootClientData?.isPremium2027,
                    yearClientData?.isPremium2027
                )
            ) ||
            billingFlagTrue(
                pickDefinedFlag(
                    rootData?.forceNewCustomerPremium,
                    rootClientData?.forceNewCustomerPremium,
                    yearClientData?.forceNewCustomerPremium
                )
            ),
        // Pro-Bono + Bestandskunden: Root-Flags (Admin) explizit mergen — sonst fehlen sie im Mandanten-Store
        proBonoActive:
            rootData?.proBonoActive === true ||
            rootClientData?.proBonoActive === true ||
            yearClientData?.proBonoActive === true ||
            rootData?.proBonoChecked === true ||
            rootClientData?.proBonoChecked === true ||
            yearClientData?.proBonoChecked === true,
        proBonoChecked:
            rootData?.proBonoActive === true ||
            rootClientData?.proBonoActive === true ||
            yearClientData?.proBonoActive === true ||
            rootData?.proBonoChecked === true ||
            rootClientData?.proBonoChecked === true ||
            yearClientData?.proBonoChecked === true,
        proBonoUntil:
            rootData?.proBonoUntil ||
            rootClientData?.proBonoUntil ||
            yearClientData?.proBonoUntil ||
            rootData?.proBonoEndDate ||
            rootClientData?.proBonoEndDate ||
            yearClientData?.proBonoEndDate ||
            '',
        proBonoEndDate:
            rootData?.proBonoEndDate ||
            rootClientData?.proBonoEndDate ||
            yearClientData?.proBonoEndDate ||
            rootData?.proBonoUntil ||
            rootClientData?.proBonoUntil ||
            yearClientData?.proBonoUntil ||
            '',
        isBestandskunde: (() => {
            const v = pickDefinedFlag(
                rootData?.isBestandskunde,
                rootClientData?.isBestandskunde,
                yearClientData?.isBestandskunde
            );
            return v === undefined ? undefined : billingFlagTrue(v);
        })(),
        umsatzsteuerbefreit: (() => {
            const v = pickDefinedFlag(
                rootData?.umsatzsteuerbefreit,
                rootClientData?.umsatzsteuerbefreit,
                yearClientData?.umsatzsteuerbefreit,
                rootData?.isVatExempt,
                rootClientData?.isVatExempt,
                yearClientData?.isVatExempt
            );
            return v === undefined ? false : billingFlagTrue(v);
        })(),
        isVatExempt: (() => {
            const v = pickDefinedFlag(
                rootData?.umsatzsteuerbefreit,
                rootClientData?.umsatzsteuerbefreit,
                yearClientData?.umsatzsteuerbefreit,
                rootData?.isVatExempt,
                rootClientData?.isVatExempt,
                yearClientData?.isVatExempt
            );
            return v === undefined ? false : billingFlagTrue(v);
        })(),
        isUnlockedByAdmin: isUnlocked,
        mandantNr: fetchedMandantNr,
        betreuer: fetchedBetreuer,
        vorname: fetchedVorname,
        nachname: fetchedNachname,
        firmenname: fetchedFirmenname,
        salonname: fetchedFirmenname, 
        email: fetchedEmail,
        mailadresse: fetchedEmail,
        geschaeftsjahre: fetchedGeschaeftsjahre,
        lizenzDetails: { typ: forcedLizenzTyp },
        basicChecked: forcedBasicChecked,
        insiderTeamleaderStandardChecked: forcedInsiderChecked,
        // Union aller Quellen — sonst gewinnt z. B. leeres/altes Root-Array und PEPlan fehlt im Menü
        unlockedPremiumTools: (() => {
            const merge = (...lists: unknown[]) => {
                const out: string[] = [];
                for (const list of lists) {
                    if (!Array.isArray(list)) continue;
                    for (const id of list) {
                        if (typeof id === 'string' && id && !out.includes(id)) out.push(id);
                    }
                }
                return out;
            };
            return merge(
                rootData?.unlockedPremiumTools,
                rootClientData?.unlockedPremiumTools,
                yearClientData?.unlockedPremiumTools
            );
        })(),
        // Freischalt-Daten & Tarif-Typen: Root + Jahresakte mergen, damit Root-Patche nie verloren gehen
        premiumToolActivatedAt: {
            ...(yearClientData?.premiumToolActivatedAt &&
            typeof yearClientData.premiumToolActivatedAt === 'object'
                ? yearClientData.premiumToolActivatedAt
                : {}),
            ...(rootClientData?.premiumToolActivatedAt &&
            typeof rootClientData.premiumToolActivatedAt === 'object'
                ? rootClientData.premiumToolActivatedAt
                : {}),
        },
        premiumToolPlanType: {
            ...(yearClientData?.premiumToolPlanType &&
            typeof yearClientData.premiumToolPlanType === 'object'
                ? yearClientData.premiumToolPlanType
                : {}),
            ...(rootClientData?.premiumToolPlanType &&
            typeof rootClientData.premiumToolPlanType === 'object'
                ? rootClientData.premiumToolPlanType
                : {}),
        },
        // Master-PIN: Root-Top-Level > Root-clientData > Jahr-clientData
        pinLockConfig: (() => {
            const pick =
                (rootData?.pinLockConfig && typeof rootData.pinLockConfig === 'object'
                    ? rootData.pinLockConfig
                    : null) ||
                (rootClientData?.pinLockConfig && typeof rootClientData.pinLockConfig === 'object'
                    ? rootClientData.pinLockConfig
                    : null) ||
                (yearClientData?.pinLockConfig && typeof yearClientData.pinLockConfig === 'object'
                    ? yearClientData.pinLockConfig
                    : null);
            if (!pick) {
                return { isPinActive: false, pinHash: '', lockedPageIds: [] as string[] };
            }
            return {
                isPinActive: pick.isPinActive === true,
                pinHash: typeof pick.pinHash === 'string' ? pick.pinHash : '',
                lockedPageIds: Array.isArray(pick.lockedPageIds)
                    ? pick.lockedPageIds.filter((id: unknown) => typeof id === 'string')
                    : [],
                updatedAt: typeof pick.updatedAt === 'string' ? pick.updatedAt : undefined,
            };
        })(),
        // Sentinel: Root (Admin-Radar) hat Vorrang — Jahr-clientData darf Mute nicht wiederbeleben
        sentinelMuted:
            rootData?.sentinelMuted === true || rootClientData?.sentinelMuted === true
                ? true
                : rootData?.sentinelMuted === false || rootClientData?.sentinelMuted === false
                    ? false
                    : yearClientData?.sentinelMuted === true,
        sentinelSnoozedUntil:
            rootData?.sentinelSnoozedUntil ??
            rootClientData?.sentinelSnoozedUntil ??
            yearClientData?.sentinelSnoozedUntil ??
            0,
        sentinelLastAction:
            rootData?.sentinelLastAction ??
            rootClientData?.sentinelLastAction ??
            yearClientData?.sentinelLastAction,
        sentinelLastActionAt:
            rootData?.sentinelLastActionAt ??
            rootClientData?.sentinelLastActionAt ??
            yearClientData?.sentinelLastActionAt,
        sentinelLastActionByEmail:
            rootData?.sentinelLastActionByEmail ??
            rootClientData?.sentinelLastActionByEmail ??
            yearClientData?.sentinelLastActionByEmail,
        sentinelLastActionByName:
            rootData?.sentinelLastActionByName ??
            rootClientData?.sentinelLastActionByName ??
            yearClientData?.sentinelLastActionByName,
        sentinelLastActionByUid:
            rootData?.sentinelLastActionByUid ??
            rootClientData?.sentinelLastActionByUid ??
            yearClientData?.sentinelLastActionByUid,
        sentinelLastActionDays:
            rootData?.sentinelLastActionDays ??
            rootClientData?.sentinelLastActionDays ??
            yearClientData?.sentinelLastActionDays,
    };

    const activeYearObj = mergedClientData.geschaeftsjahre?.find((j: any) => j.jahr.toString() === abrufJahr);
    if (activeYearObj && activeYearObj.status === 'abgeschlossen' && activeYearObj.archivDaten) {
        mergedClientData.gesamtleistungData = activeYearObj.archivDaten.gesamtleistungData || mergedClientData.gesamtleistungData;
        mergedClientData.ulHalbjahr1 = activeYearObj.archivDaten.ulHalbjahr1 || mergedClientData.ulHalbjahr1;
        mergedClientData.ulHalbjahr2 = activeYearObj.archivDaten.ulHalbjahr2 || mergedClientData.ulHalbjahr2;
        mergedClientData.mitarbeiterLeistung = activeYearObj.archivDaten.mitarbeiterLeistung || mergedClientData.mitarbeiterLeistung;
    }

    mergedClientData = injectFestsetzungsSnapshots(mergedClientData, yearData?.festsetzungsSnapshots);
    mergedClientData = applyLocalFestsetzungBackup(mergedClientData);
    mergedClientData = mergeLocalLeistungSnapshots(mergedClientData);
    mergedClientData = applyPendingFestsetzungen(mergedClientData);
    mergedClientData = sanitizeClientData(mergedClientData);

    // Filial-Registry immer vom Root (nicht vom Filial-Jahresdoc)
    const branchMeta = resolveBranchMeta(rootData || {}, mergedClientData);
    mergedClientData.hasBranches = branchMeta.hasBranches;
    mergedClientData.branches = branchMeta.branches;
    mergedClientData.activeBranchId = branchMeta.activeBranchId;
    if (branchMeta.activeBranch?.name) {
        mergedClientData.branchName = branchMeta.activeBranch.name;
    }
    // TeamPro+-Gating filialübergreifend vom Root spiegeln
    if (rootData?.aggregatedNettoMonatsumsatz != null || rootData?.clientData?.aggregatedNettoMonatsumsatz != null) {
        mergedClientData.aggregatedNettoMonatsumsatz = Number(
            rootData?.aggregatedNettoMonatsumsatz ?? rootData?.clientData?.aggregatedNettoMonatsumsatz
        ) || 0;
    }
    if (rootData?.nettoMonatsumsatz != null || rootData?.clientData?.nettoMonatsumsatz != null) {
        mergedClientData.nettoMonatsumsatz = Number(
            rootData?.nettoMonatsumsatz ?? rootData?.clientData?.nettoMonatsumsatz
        ) || 0;
    }
    const rootPlus = rootData?.isTeamProPlus === true
        || rootData?.clientData?.isTeamProPlus === true
        || rootData?.insiderTeamleaderPlusChecked === true
        || rootData?.clientData?.insiderTeamleaderPlusChecked === true;
    if (rootPlus) {
        mergedClientData.isTeamProPlus = true;
        mergedClientData.insiderTeamleaderPlusChecked = true;
        mergedClientData.insiderTeamleaderStandardChecked = false;
    } else if (branchMeta.hasBranches) {
        mergedClientData.isTeamProPlus = !!rootData?.isTeamProPlus || !!rootData?.clientData?.isTeamProPlus;
    }
    try {
        if (branchMeta.hasBranches && branchMeta.activeBranchId) {
            sessionStorage.setItem('CURRENT_BRANCH', branchMeta.activeBranchId);
            localStorage.setItem('CURRENT_BRANCH', branchMeta.activeBranchId);
        }
    } catch (_) { /* ignore */ }

    // Frische Aggregat-Prüfung (nicht-blockierend für Timeout-Races: await hier ist OK im Load)
    try {
            const gating = await recomputeAndPersistFilialTeamProPlus(
                uid,
                abrufJahr,
                rootData,
                sysData?.settings?.global || useAppStore.getState()?.settings?.global
            );
            if (gating) {
                mergedClientData.aggregatedNettoMonatsumsatz = gating.aggregated;
                mergedClientData.nettoMonatsumsatz = gating.aggregated;
                mergedClientData.isTeamProPlus = gating.isTeamProPlus;
                if (gating.isTeamProPlus) {
                    mergedClientData.insiderTeamleaderPlusChecked = true;
                    mergedClientData.insiderTeamleaderStandardChecked = false;
                    mergedClientData.packageTier = 'TeamPro+';
                    mergedClientData.tarifStatus = 'TeamPro+';
                } else if (
                    String(mergedClientData.packageTier || mergedClientData.tarifStatus || '').startsWith('TeamPro')
                ) {
                    mergedClientData.packageTier = 'TeamPro';
                    mergedClientData.tarifStatus = 'TeamPro';
                }
            }
        } catch (_) { /* ignore */ }

    // Vektor 1: Purge nur wenn Jahresakte physisch existiert (SoT steht) —
    // sonst würden Root-Ops vor dem ersten Year-Seed gelöscht (Reload-Race).
    // Ohne Year-Doc: Purge erfolgt beim nächsten erfolgreichen Root-Sync.
    if (yearDocExisted) {
        try {
            void purgeLegacyRootOpsData(uid);
        } catch (_) { /* ignore */ }
    }

    return {
        clientData: mergedClientData,
        settings: mergeWithDefaultSettings(sysData?.settings || null),
        team: sysData?.team || [],
        appSettings: sysData?.appSettings || {},
        savedSnapshots: yearData?.savedSnapshots || rootData?.savedSnapshots || [],
        serverVerified: true,
    };
};

export const executeGlobalMassFreeze = async (stichtagIndex: number, currentSettings: any): Promise<number> => {
    try {
        const mandantenSnapshot = await getDocs(collection(db, 'kalkulation_mandanten'));
        let updateCount = 0;

        for (const docSnap of mandantenSnapshot.docs) {
            const uid = docSnap.id;
            if (uid === 'system_data' || uid === 'master') continue;

            const rootData = docSnap.data();
            const aktivesJahr = rootData.aktivesJahr || new Date().getFullYear().toString();

            const yearDocRef = doc(db, 'kalkulation_mandanten', uid, 'jahre', aktivesJahr);
            const yearDocSnap = await getDoc(yearDocRef);

            if (yearDocSnap.exists()) {
                let yearData = yearDocSnap.data();
                let clientData = yearData.clientData;

                if (!clientData || !clientData.gesamtleistungData) continue;

                const rawLand = clientData.land || clientData.Land || 'DE';
                const activeLand = String(rawLand).toUpperCase();
                const base = currentSettings?.countries?.[activeLand] || currentSettings?.countries?.DE || {};
                const overrides = currentSettings?.countryOverrides?.[activeLand] || {};
                const mwstSatz = overrides.MwSt_Satz ?? base.MwSt_Satz ?? (activeLand === 'CH' ? 8.1 : (activeLand === 'AT' ? 20 : 19));

                const activeMwstFaktor = clientData.isVatExempt ? 1 : 1 + (mwstSatz / 100);
                const ruecklagenZielFaktor = (currentSettings?.global?.Liquiditaet || 0) / 100;
                const gesamtUnternehmerlohn = (clientData.unternehmerData?.entnahme || 0) + (clientData.unternehmerData?.sozialkosten || 0);

                const lfData = clientData.lohnfaktorData || {};
                const mindestumsatz1 = lfData.mindestumsatz1 || 0;
                const mindestumsatz2 = lfData.mindestumsatz2 || 0;

                const newGesamtleistungData = (clientData.gesamtleistungData || []).map((monat: any, index: number) => {
                    if (index < stichtagIndex) {
                        if (monat.abgeschlossen === true) {
                            return monat; 
                        }
                        return {
                            ...monat,
                            abgeschlossen: true,
                            frozenCalculations: {
                                ...(monat.frozenCalculations || {}),
                                mwstFaktor: activeMwstFaktor,
                                ruecklagenZielFaktor: ruecklagenZielFaktor,
                                mindestumsatzBase: index < 6 ? mindestumsatz1 : mindestumsatz2,
                                privatentnahme: gesamtUnternehmerlohn
                            }
                        };
                    }
                    return monat;
                });

                const processHalbjahr = (halbjahrData: any[], startAbsoluteIndex: number) => {
                    if (!Array.isArray(halbjahrData)) return [];
                    return halbjahrData.map((monat: any, index: number) => {
                        const absoluteIndex = startAbsoluteIndex + index;
                        if (absoluteIndex < stichtagIndex) {
                            if (monat.festmachen === true) {
                                return monat;
                            }
                            return { ...monat, festmachen: true, lockedSnapshot: safeClone(monat) };
                        }
                        return monat;
                    });
                };

                const newUlHalbjahr1 = processHalbjahr(clientData.ulHalbjahr1 || [], 0);
                const newUlHalbjahr2 = processHalbjahr(clientData.ulHalbjahr2 || [], 6);

                const newMitarbeiterLeistung = { ...(clientData.mitarbeiterLeistung || {}) };
                Object.keys(newMitarbeiterLeistung).forEach(empId => {
                    const empData = newMitarbeiterLeistung[empId];
                    if (empData) {
                        newMitarbeiterLeistung[empId] = {
                            ...empData,
                            halbjahr1: processHalbjahr(empData.halbjahr1 || [], 0),
                            halbjahr2: processHalbjahr(empData.halbjahr2 || [], 6)
                        };
                    }
                });

                clientData.gesamtleistungData = newGesamtleistungData;
                clientData.ulHalbjahr1 = newUlHalbjahr1;
                clientData.ulHalbjahr2 = newUlHalbjahr2;
                clientData.mitarbeiterLeistung = newMitarbeiterLeistung;
                clientData.frozenUntilMonth = stichtagIndex;

                const cleanClientData = safeClone(clientData);

                await setDoc(yearDocRef, { clientData: cleanClientData }, { merge: true });
                updateCount++;
            }
        }
        return updateCount;
    } catch (error: any) {
        console.error("Massen-Freeze Fehler:", error);
        return -1;
    }
};

export const executeGlobalMassUnfreeze = async (): Promise<number> => {
    try {
        const mandantenSnapshot = await getDocs(collection(db, 'kalkulation_mandanten'));
        let updateCount = 0;

        for (const docSnap of mandantenSnapshot.docs) {
            const uid = docSnap.id;
            if (uid === 'system_data' || uid === 'master') continue;

            const rootData = docSnap.data();
            const aktivesJahr = rootData.aktivesJahr || new Date().getFullYear().toString();

            const yearDocRef = doc(db, 'kalkulation_mandanten', uid, 'jahre', aktivesJahr);
            const yearDocSnap = await getDoc(yearDocRef);

            if (yearDocSnap.exists()) {
                let yearData = yearDocSnap.data();
                let clientData = yearData.clientData;

                if (!clientData) continue;

                if (Array.isArray(clientData.gesamtleistungData)) {
                    clientData.gesamtleistungData = clientData.gesamtleistungData.map((m: any) => {
                        const newM = { ...m, abgeschlossen: false };
                        delete newM.frozenCalculations;
                        return newM;
                    });
                }

                const unfreezeHalbjahr = (halbjahrData: any[]) => {
                    if (!Array.isArray(halbjahrData)) return [];
                    return halbjahrData.map((m: any) => {
                        const newM = { ...m, festmachen: false };
                        delete newM.lockedSnapshot;
                        return newM;
                    });
                };

                clientData.ulHalbjahr1 = unfreezeHalbjahr(clientData.ulHalbjahr1);
                clientData.ulHalbjahr2 = unfreezeHalbjahr(clientData.ulHalbjahr2);

                if (clientData.mitarbeiterLeistung) {
                    Object.keys(clientData.mitarbeiterLeistung).forEach(empId => {
                        const empData = clientData.mitarbeiterLeistung[empId];
                        if (empData) {
                            clientData.mitarbeiterLeistung[empId] = {
                                ...empData,
                                halbjahr1: unfreezeHalbjahr(empData.halbjahr1),
                                halbjahr2: unfreezeHalbjahr(empData.halbjahr2)
                            };
                        }
                    });
                }

                delete clientData.frozenUntilMonth;
                const cleanClientData = safeClone(clientData);
                
                await setDoc(yearDocRef, { clientData: cleanClientData }, { merge: true });

                // FIREBASE DELETE FIELD FIX
                try {
                    await updateDoc(yearDocRef, { "clientData.frozenUntilMonth": deleteField() });
                    await updateDoc(doc(db, 'kalkulation_mandanten', uid), { 
                        "clientData.frozenUntilMonth": deleteField(),
                        "frozenUntilMonth": deleteField()
                    });
                } catch(e) {}

                updateCount++;
            }
        }
        return updateCount;
    } catch (error: any) {
        console.error("Massen-Unfreeze Fehler:", error);
        return -1;
    }
};